import { FC, useState, useEffect, useCallback, useMemo, useRef } from 'react'
import { useSearchParams, Link } from 'react-router-dom'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import rehypeRaw from 'rehype-raw'
import rehypeSlug from 'rehype-slug'
import MavNavBar, { MAV_NAV_CSS } from '../components/MavNavBar'

// ── Markdown document imports (CRA resolves to asset URLs) ──
import mdUserGuide from '../mav/UserGuide.md'
import mdGettingStarted from '../mav/GettingStarted.md'
import mdMainMenu from '../mav/MainMenu.md'
import mdMainMenubar from '../mav/MainMenubar.md'
import mdMenus from '../mav/Menus.md'
import mdSessions from '../mav/Sessions.md'
import mdSessionEditor from '../mav/SessionEditor.md'
import mdSessionSharing from '../mav/SessionSharing.md'
import mdSessionManager from '../mav/SessionManager.md'
import mdVisualizer from '../mav/Visualizer.md'
import mdPreferences from '../mav/Preferences.md'
import mdHotkeys from '../mav/Hotkeys.md'
import mdSupport from '../mav/Support.md'
import mdDownloads from '../mav/Downloads.md'
import mdMAVLauncher from '../mav/MAVLauncher.md'
import mdWebLauncher from '../mav/WebLauncher.md'
import mdVersionMenu from '../mav/VersionMenu.md'
import mdVizAnalysisSets from '../mav/VisualizerAnalysisSets.md'
import mdVizLayouts from '../mav/VisualizerLayouts.md'
import mdVizViewPanes from '../mav/VisualizerViewPanes.md'
import mdVizViewNav from '../mav/VisualizerViewNavigation.md'
import mdVizViewToolbar from '../mav/VisualizerViewToolbar.md'
import mdVizViewLinkGroups from '../mav/VisualizerViewLinkGroups.md'
import mdVizViewControls from '../mav/VisualizerViewControls.md'
import mdVizViewVariables from '../mav/VisualizerViewVariables.md'
import mdVizViewSlices from '../mav/VisualizerViewSlices.md'
import mdVizSurfacePartsList from '../mav/VisualizerSurfacePartsList.md'
import mdVizSurfacePlotGraph from '../mav/VisualizerSurfacePlotGraph.md'
import mdVizFloorplots from '../mav/VisualizerFloorplots.md'
import mdVizDisplayPrefs from '../mav/VisualizerDisplayPreferences.md'
import mdVizEnvPrefs from '../mav/VisualizerEnvironmentPreferences.md'
import mdVizMediaCapture from '../mav/VisualizerMediaCapture.md'
import mdVizSlices from '../mav/VisualizerSlicesPanel.md'
import mdVizSlicePrefs from '../mav/VisualizerSlicePreferences.md'
import mdVizColorMaps from '../mav/VisualizerColorMapsPanel.md'
import mdVizColorMapPrefs from '../mav/VisualizerColorMapPreferences.md'
import mdVizLIC from '../mav/VisualizerLICPanel.md'
import mdVizStreamlines from '../mav/VisualizerStreamlines.md'
import mdVizVolumeClipping from '../mav/VisualizerVolumeClipping.md'
import mdVizGlyphs from '../mav/VisualizerGlyphs.md'
import mdVizAnimation from '../mav/VisualizerAnimation.md'
import mdSliceResults from '../mav/SliceResults.md'
import mdFAQ from '../mav/FAQ.md'

// ── Image imports ──
import imgStartup from '../mav/images/startup.jpg'
import imgMainMenu from '../mav/images/main_menu.jpg'
import imgMainMenu2 from '../mav/images/main_menu2.jpg'
import imgMainMenubarCenter from '../mav/images/main_menubar_center.jpg'
import imgMainMenubarRight from '../mav/images/main_menubar_right.jpg'
import imgMemoryPopup from '../mav/images/memory_popup.jpg'
import imgPreferencesMenu from '../mav/images/preferences_menu.jpg'
import imgSessionEditor from '../mav/images/session_editor.jpg'
import imgSessionEditorCloudCar from '../mav/images/session_editor_cloud_car.jpg'
import imgSessionEditorGrouped from '../mav/images/session_editor_grouped.jpg'
import imgSessionEditorGroupByRuns from '../mav/images/session_editor_group_by_runs.jpg'
import imgSessionEditorLocal from '../mav/images/session_editor_local.jpg'
import imgSessionEditorNetwork from '../mav/images/session_editor_network.jpg'
import imgSessionEditorProject from '../mav/images/session_editor_project.jpg'
import imgSessionEditorRuns from '../mav/images/session_editor_runs.jpg'
import imgSessionEditorRunsSelected from '../mav/images/session_editor_runs_selected.jpg'
import imgSessionEditorStates from '../mav/images/session_editor_states.jpg'
import imgSessionEditorStatesAdded from '../mav/images/session_editor_states_added.jpg'
import imgSessionManager from '../mav/images/session_manager.jpg'
import imgSessionManagerMore from '../mav/images/session_manager_more.jpg'
import imgUserMenu from '../mav/images/user_menu.jpg'
import imgMavLauncherIcon from '../mav/images/mav-launcher-icon.png'
import imgMavAeroSplash from '../mav/images/mav-aero_splash.jpg'
import imgVizAnalysisSets from '../mav/images/visualizer_analysis_sets.jpg'
import imgVizAnalysisSetsPanel from '../mav/images/visualizer_analysis_sets_panel.jpg'
import imgVizLayouts from '../mav/images/visualizer_layouts.jpg'
import imgVizLayoutsReplace from '../mav/images/visualizer_layouts_replace.jpg'
import imgVizViewPanes from '../mav/images/visualizer_view_panes.jpg'
import imgVizViewPanesHeader from '../mav/images/visualizer_view_panes_header.jpg'
import imgVizToolbar from '../mav/images/visualizer_toolbar.jpg'
import imgVizLinkGroups from '../mav/images/visualizer_link_groups.jpg'
import imgVizLinkGroupsEdit from '../mav/images/visualizer_link_groups_edit.jpg'
import imgVizViewControls from '../mav/images/visualizer_view_controls.jpg'
import imgVizViewControlsButtons from '../mav/images/visualizer_view_controls_buttons.jpg'
import imgVizViewVariables from '../mav/images/visualizer_view_variables.jpg'
import imgVizSurfaceParts from '../mav/images/visualizer_surface_parts.jpg'
import imgVizSurfacePartsColored from '../mav/images/visualizer_surface_parts_colored.jpg'
import imgVizSurfacePlotGraph from '../mav/images/visualizer_surface_plot_graph.jpg'
import imgVizSurfacePlotGraphSettings from '../mav/images/visualizer_surface_plot_graph_settings.jpg'
import imgVizFloorPlots from '../mav/images/visualizer_floor_plots.jpg'
import imgVizFloorPlotsControls from '../mav/images/visualizer_floor_plots_controls.jpg'
import imgVizSlicesPanel from '../mav/images/visualizer_slices_panel.jpg'
import imgVizSlicePrefs from '../mav/images/visualizer_slice_preferences.jpg'
import imgVizCfdCoords from '../mav/images/visualizer_cfd_coords.jpg'
import imgVizFullscreenPresentingMode from '../mav/images/visualizer_fullscreen_presenting_mode.jpg'
import imgVizPresetOverridesWatermark from '../mav/images/visualizer_preset_overrides_watermark.jpg'
import imgVizColorMapPrefs from '../mav/images/visualizer_color_map_preferences.jpg'
import imgVizColorMapsPanel from '../mav/images/visualizer_color_maps_panel.jpg'
import imgVizDisplayPreferences from '../mav/images/visualizer_display_preferences.jpg'
import imgVizDisplayPreferencesPin from '../mav/images/visualizer_display_preferences_pin.jpg'
import imgVizEnvironmentPreferences from '../mav/images/visualizer_environment_preferences.jpg'
import imgVizMediaExport from '../mav/images/visualizer_media_export.jpg'
import imgVizLicPanel from '../mav/images/visualizer_lic_panel.jpg'
import imgVizStreamlinesPanel from '../mav/images/visualizer_streamlines_panel.jpg'
import imgVizStreamlinesPanel2 from '../mav/images/visualizer_streamlines_panel_2.jpg'
import imgVizSlices from '../mav/images/visualizer_slices.jpg'
import imgVizViewNavigation from '../mav/images/visualizer_view_navigation.jpg'
import imgSliceBoundingBoxAxisScale from '../mav/images/MAV-Aero_SliceBoundingBox_AxisScale.jpg'
import imgSliceBoundingBoxViewBounds from '../mav/images/MAV-Aero_SliceBoundingBox_ViewBounds.jpg'

// ── Types ──
interface DocNode {
  id: string
  title: string
  mdUrl: string
  children?: DocNode[]
}

// ── Document tree (mirrors UserGuide.md + Visualizer.md TOC) ──
const DOC_TREE: DocNode[] = [
  { id: 'user-guide', title: 'User Guide', mdUrl: mdUserGuide },
  { id: 'getting-started', title: 'Getting Started', mdUrl: mdGettingStarted },
  {
    id: 'menus',
    title: 'Menus',
    mdUrl: mdMenus,
    children: [
      { id: 'main-menu', title: 'Main Menu', mdUrl: mdMainMenu },
      { id: 'main-menubar', title: 'Main Menubar', mdUrl: mdMainMenubar },
      { id: 'downloads', title: 'Downloads', mdUrl: mdDownloads },
      { id: 'preferences', title: 'Preferences', mdUrl: mdPreferences },
      { id: 'hotkeys', title: 'Hotkeys', mdUrl: mdHotkeys }
    ]
  },
  {
    id: 'sessions',
    title: 'Sessions',
    mdUrl: mdSessions,
    children: [
      { id: 'session-editor', title: 'Session Editor', mdUrl: mdSessionEditor },
      { id: 'session-manager', title: 'Session Manager', mdUrl: mdSessionManager },
      { id: 'session-sharing', title: 'Session Sharing', mdUrl: mdSessionSharing }
    ]
  },
  {
    id: 'visualizer',
    title: 'Visualizer',
    mdUrl: mdVisualizer,
    children: [
      { id: 'viz-analysis-sets', title: 'Analysis Sets', mdUrl: mdVizAnalysisSets },
      { id: 'viz-layouts', title: 'Layouts', mdUrl: mdVizLayouts },
      { id: 'viz-view-panes', title: 'View Panes', mdUrl: mdVizViewPanes },
      { id: 'viz-view-nav', title: 'View Navigation', mdUrl: mdVizViewNav },
      { id: 'viz-view-toolbar', title: 'View Toolbar', mdUrl: mdVizViewToolbar },
      { id: 'viz-view-link-groups', title: 'View Link Groups', mdUrl: mdVizViewLinkGroups },
      { id: 'viz-view-controls', title: 'View Controls', mdUrl: mdVizViewControls },
      { id: 'viz-view-variables', title: 'View Variables', mdUrl: mdVizViewVariables },
      { id: 'viz-view-slices', title: 'View Slices', mdUrl: mdVizViewSlices },
      { id: 'viz-surface-parts', title: 'Surface Parts List', mdUrl: mdVizSurfacePartsList },
      { id: 'viz-surface-plot', title: 'Surface Plot Graph', mdUrl: mdVizSurfacePlotGraph },
      { id: 'viz-floorplots', title: 'Floorplots', mdUrl: mdVizFloorplots },
      { id: 'viz-display-prefs', title: 'Display Options', mdUrl: mdVizDisplayPrefs },
      { id: 'viz-env-prefs', title: 'Environment Options', mdUrl: mdVizEnvPrefs },
      { id: 'viz-media-capture', title: 'Media Capture', mdUrl: mdVizMediaCapture },
      { id: 'viz-slices', title: 'Slices Panel', mdUrl: mdVizSlices },
      { id: 'viz-slice-prefs', title: 'Slice Preferences', mdUrl: mdVizSlicePrefs },
      { id: 'viz-color-maps', title: 'Color Maps Panel', mdUrl: mdVizColorMaps },
      { id: 'viz-color-map-prefs', title: 'Color Map Preferences', mdUrl: mdVizColorMapPrefs },
      { id: 'viz-lic', title: 'LIC Panel', mdUrl: mdVizLIC },
      { id: 'viz-streamlines', title: 'Streamlines Panel', mdUrl: mdVizStreamlines },
      { id: 'viz-volume-clipping', title: 'Volume Clipping', mdUrl: mdVizVolumeClipping },
      { id: 'viz-glyphs', title: 'Glyphs Panel', mdUrl: mdVizGlyphs },
      { id: 'viz-animation', title: 'Animation Panel', mdUrl: mdVizAnimation }
    ]
  },
  {
    id: 'configuration',
    title: 'Configuration',
    mdUrl: mdSliceResults,
    children: [{ id: 'slice-results', title: 'Slice Results', mdUrl: mdSliceResults }]
  },
  { id: 'mav-launcher', title: 'MAV Launcher', mdUrl: mdMAVLauncher },
  { id: 'web-launcher', title: 'Web Launcher', mdUrl: mdWebLauncher },
  { id: 'version-menu', title: 'Version Menu', mdUrl: mdVersionMenu },
  { id: 'faq', title: 'FAQ', mdUrl: mdFAQ },
  { id: 'support', title: 'Support', mdUrl: mdSupport }
]

// ── Build flat URL lookup ──
const URL_MAP: Record<string, string> = {}
;(function walk(nodes: DocNode[]) {
  for (const n of nodes) {
    URL_MAP[n.id] = n.mdUrl
    if (n.children) walk(n.children)
  }
})(DOC_TREE)
URL_MAP['search'] = ''

// ── Filename → doc-id (for cross-doc markdown links) ──
const LINK_MAP: Record<string, string> = {
  UserGuide: 'user-guide',
  GettingStarted: 'getting-started',
  Menus: 'menus',
  Sessions: 'sessions',
  MainMenu: 'main-menu',
  MainMenubar: 'main-menubar',
  SessionEditor: 'session-editor',
  SessionManager: 'session-manager',
  SessionSharing: 'session-sharing',
  Visualizer: 'visualizer',
  Preferences: 'preferences',
  Hotkeys: 'hotkeys',
  Support: 'support',
  VisualizerAnalysisSets: 'viz-analysis-sets',
  VisualizerLayouts: 'viz-layouts',
  VisualizerViewPanes: 'viz-view-panes',
  VisualizerViewNavigation: 'viz-view-nav',
  VisualizerViewToolbar: 'viz-view-toolbar',
  VisualizerViewLinkGroups: 'viz-view-link-groups',
  VisualizerViewControls: 'viz-view-controls',
  VisualizerViewVariables: 'viz-view-variables',
  VisualizerViewSlices: 'viz-view-slices',
  VisualizerSurfacePartsList: 'viz-surface-parts',
  VisualizerSurfacePlotGraph: 'viz-surface-plot',
  VisualizerFloorplots: 'viz-floorplots',
  VisualizerDisplayOptions: 'viz-display-prefs',
  VisualizerDisplayPreferences: 'viz-display-prefs',
  VisualizerEnvironmentOptions: 'viz-env-prefs',
  VisualizerEnvironmentPreferences: 'viz-env-prefs',
  VisualizerMediaCapture: 'viz-media-capture',
  VisualizerSlicesPanel: 'viz-slices',
  VisualizerSlicePreferences: 'viz-slice-prefs',
  VisualizerColorMapsPanel: 'viz-color-maps',
  VisualizerColorMapPanel: 'viz-color-maps',
  VisualizerColorMapPreferences: 'viz-color-map-prefs',
  VisualizerLICPanel: 'viz-lic',
  VisualizerStreamlines: 'viz-streamlines',
  VisualizerVolumeClipping: 'viz-volume-clipping',
  VisualizerGlyphs: 'viz-glyphs',
  VisualizerAnimation: 'viz-animation',
  SliceResults: 'slice-results',
  Downloads: 'downloads',
  MAVLauncher: 'mav-launcher',
  WebLauncher: 'web-launcher',
  VersionMenu: 'version-menu',
  FAQ: 'faq'
}

// ── Image path → bundled URL ──
const IMAGE_MAP: Record<string, string> = {
  'images/startup.jpg': imgStartup,
  'images/main_menu.jpg': imgMainMenu,
  'images/main_menu2.jpg': imgMainMenu2,
  'images/main_menubar_center.jpg': imgMainMenubarCenter,
  'images/main_menubar_right.jpg': imgMainMenubarRight,
  'images/memory_popup.jpg': imgMemoryPopup,
  'images/preferences_menu.jpg': imgPreferencesMenu,
  'images/session_editor.jpg': imgSessionEditor,
  'images/session_editor_cloud_car.jpg': imgSessionEditorCloudCar,
  'images/session_editor_grouped.jpg': imgSessionEditorGrouped,
  'images/session_editor_group_by_runs.jpg': imgSessionEditorGroupByRuns,
  'images/session_editor_local.jpg': imgSessionEditorLocal,
  'images/session_editor_network.jpg': imgSessionEditorNetwork,
  'images/session_editor_project.jpg': imgSessionEditorProject,
  'images/session_editor_runs.jpg': imgSessionEditorRuns,
  'images/session_editor_runs_selected.jpg': imgSessionEditorRunsSelected,
  'images/session_editor_states.jpg': imgSessionEditorStates,
  'images/session_editor_states_added.jpg': imgSessionEditorStatesAdded,
  'images/session_manager.jpg': imgSessionManager,
  'images/session_manager_more.jpg': imgSessionManagerMore,
  'images/user_menu.jpg': imgUserMenu,
  'images/mav-launcher-icon.png': imgMavLauncherIcon,
  'images/mav-aero_splash.jpg': imgMavAeroSplash,
  'images/visualizer_analysis_sets.jpg': imgVizAnalysisSets,
  'images/visualizer_analysis_sets_panel.jpg': imgVizAnalysisSetsPanel,
  'images/visualizer_layouts.jpg': imgVizLayouts,
  'images/visualizer_layouts_replace.jpg': imgVizLayoutsReplace,
  'images/visualizer_view_panes.jpg': imgVizViewPanes,
  'images/visualizer_view_panes_header.jpg': imgVizViewPanesHeader,
  'images/visualizer_toolbar.jpg': imgVizToolbar,
  'images/visualizer_link_groups.jpg': imgVizLinkGroups,
  'images/visualizer_link_groups_edit.jpg': imgVizLinkGroupsEdit,
  'images/visualizer_view_controls.jpg': imgVizViewControls,
  'images/visualizer_view_controls_buttons.jpg': imgVizViewControlsButtons,
  'images/visualizer_view_variables.jpg': imgVizViewVariables,
  'images/visualizer_surface_parts.jpg': imgVizSurfaceParts,
  'images/visualizer_surface_parts_colored.jpg': imgVizSurfacePartsColored,
  'images/visualizer_surface_plot_graph.jpg': imgVizSurfacePlotGraph,
  'images/visualizer_surface_plot_graph_settings.jpg': imgVizSurfacePlotGraphSettings,
  'images/visualizer_floor_plots.jpg': imgVizFloorPlots,
  'images/visualizer_floor_plots_controls.jpg': imgVizFloorPlotsControls,
  'images/visualizer_slices_panel.jpg': imgVizSlicesPanel,
  'images/visualizer_slice_preferences.jpg': imgVizSlicePrefs,
  'images/visualizer_cfd_coords.jpg': imgVizCfdCoords,
  'images/visualizer_fullscreen_presenting_mode.jpg': imgVizFullscreenPresentingMode,
  'images/visualizer_preset_overrides_watermark.jpg': imgVizPresetOverridesWatermark,
  'images/visualizer_color_map_preferences.jpg': imgVizColorMapPrefs,
  'images/visualizer_color_maps_panel.jpg': imgVizColorMapsPanel,
  'images/visualizer_display_preferences.jpg': imgVizDisplayPreferences,
  'images/visualizer_display_preferences_pin.jpg': imgVizDisplayPreferencesPin,
  'images/visualizer_environment_preferences.jpg': imgVizEnvironmentPreferences,
  'images/visualizer_media_export.jpg': imgVizMediaExport,
  'images/visualizer_lic_panel.jpg': imgVizLicPanel,
  'images/visualizer_streamlines_panel.jpg': imgVizStreamlinesPanel,
  'images/visualizer_streamlines_panel_2.jpg': imgVizStreamlinesPanel2,
  'images/visualizer_slices.jpg': imgVizSlices,
  'images/visualizer_view_navigation.jpg': imgVizViewNavigation,
  'images/MAV-Aero_SliceBoundingBox_AxisScale.jpg': imgSliceBoundingBoxAxisScale,
  'images/MAV-Aero_SliceBoundingBox_ViewBounds.jpg': imgSliceBoundingBoxViewBounds
}

// ── Chevron SVG ──
const ChevronIcon: FC<{ expanded: boolean }> = ({ expanded }) => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    style={{
      transition: 'transform 0.2s',
      transform: expanded ? 'rotate(90deg)' : 'rotate(0deg)',
      flexShrink: 0
    }}>
    <polyline points="9 18 15 12 9 6" />
  </svg>
)

/* ═══════════════════════════════════════════════════════
   Component
   ═══════════════════════════════════════════════════════ */
// ── Flat list of all searchable docs ──
const ALL_DOCS: { id: string; title: string; mdUrl: string }[] = []
;(function flatWalk(nodes: DocNode[]) {
  for (const n of nodes) {
    ALL_DOCS.push({ id: n.id, title: n.title, mdUrl: n.mdUrl })
    if (n.children) flatWalk(n.children)
  }
})(DOC_TREE)

/** Convert ~~~panel type=X ... ~~~ fences so the type is embedded in the content */
function preprocessPanels(md: string): string {
  return md.replace(
    /~~~panel\s+type=(\w+)\s*\n([\s\S]*?)~~~/g,
    (_: string, type: string, body: string) => '~~~panel\n%%PANEL:' + type + '%%\n' + body + '~~~'
  )
}

const MAVAero: FC = () => {
  const [searchParams, setSearchParams] = useSearchParams()
  const initialDoc = URL_MAP[searchParams.get('doc') ?? '']
    ? searchParams.get('doc')!
    : 'user-guide'
  const [selectedDoc, setSelectedDoc] = useState(initialDoc)
  const [content, setContent] = useState('')
  const [loading, setLoading] = useState(true)
  const [expandedNodes, setExpandedNodes] = useState<Set<string>>(new Set())
  const [searchPageResults, setSearchPageResults] = useState<
    { docId: string; title: string; snippet: string }[]
  >([])
  const [searchPageLoading, setSearchPageLoading] = useState(false)
  const contentRef = useRef<HTMLDivElement>(null)
  const cache = useRef<Record<string, string>>({})
  const mdComponentsRef = useRef<any>(null)

  /* ── Fetch markdown content ── */
  const loadDoc = useCallback(async (docId: string) => {
    if (cache.current[docId]) {
      setContent(cache.current[docId])
      setLoading(false)
      return
    }
    setLoading(true)
    try {
      const url = URL_MAP[docId]
      if (!url) {
        setContent('# Not Found\nThis document could not be found.')
        return
      }
      const res = await fetch(url)
      const text = await res.text()
      cache.current[docId] = text
      setContent(text)
    } catch {
      setContent('# Error\nFailed to load document.')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    if (selectedDoc === 'search') {
      setLoading(false)
      return
    }
    loadDoc(selectedDoc)
  }, [selectedDoc, loadDoc])

  // scroll content to top on doc change
  useEffect(() => {
    contentRef.current?.scrollTo(0, 0)
  }, [content])

  useEffect(() => {
    if (selectedDoc === 'search') contentRef.current?.scrollTo(0, 0)
  }, [selectedDoc])

  /* ── Search results page ── */
  useEffect(() => {
    if (selectedDoc !== 'search') {
      setSearchPageResults([])
      return
    }
    const q = searchParams.get('q') ?? ''
    if (!q.trim()) {
      setSearchPageResults([])
      setSearchPageLoading(false)
      return
    }
    let cancelled = false
    setSearchPageLoading(true)
    ;(async () => {
      const results: { docId: string; title: string; snippet: string }[] = []
      const lowerQ = q.toLowerCase()
      for (const doc of ALL_DOCS) {
        if (cancelled) return
        if (!cache.current[doc.id]) {
          try {
            const res = await fetch(doc.mdUrl)
            cache.current[doc.id] = await res.text()
          } catch {
            continue
          }
        }
        const text = cache.current[doc.id]
        const lowerText = text.toLowerCase()
        const idx = lowerText.indexOf(lowerQ)
        if (idx !== -1 || doc.title.toLowerCase().includes(lowerQ)) {
          let snippet = ''
          if (idx !== -1) {
            const start = Math.max(0, idx - 80)
            const end = Math.min(text.length, idx + q.length + 300)
            snippet =
              (start > 0 ? '…' : '') +
              text
                .slice(start, end)
                .replace(/[#*`~_|]/g, '')
                .replace(/\n+/g, ' ') +
              (end < text.length ? '…' : '')
          } else {
            snippet =
              text
                .slice(0, 320)
                .replace(/[#*`~_|]/g, '')
                .replace(/\n+/g, ' ') + '…'
          }
          results.push({ docId: doc.id, title: doc.title, snippet })
        }
      }
      if (!cancelled) {
        setSearchPageResults(results)
        setSearchPageLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [selectedDoc, searchParams])

  /* ── Navigation helpers ── */
  const selectDoc = useCallback(
    (docId: string) => {
      setSelectedDoc(docId)
      setSearchParams({ doc: docId })
      // auto-expand parent when a child is picked
      for (const node of DOC_TREE) {
        if (node.children?.some(c => c.id === docId)) {
          setExpandedNodes(prev => new Set([...prev, node.id]))
          break
        }
      }
    },
    [setSearchParams]
  )

  // Sync selectedDoc when the URL changes (browser back/forward)
  useEffect(() => {
    const docId = searchParams.get('doc') ?? ''
    const resolved = URL_MAP[docId] !== undefined ? docId : 'user-guide'
    if (resolved !== selectedDoc) {
      setSelectedDoc(resolved)
      for (const node of DOC_TREE) {
        if (node.children?.some(c => c.id === resolved)) {
          setExpandedNodes(prev => new Set([...prev, node.id]))
          break
        }
      }
    }
  }, [searchParams])

  const toggleExpand = useCallback((nodeId: string) => {
    setExpandedNodes(prev => {
      const next = new Set(prev)
      next.has(nodeId) ? next.delete(nodeId) : next.add(nodeId)
      return next
    })
  }, [])

  /* ── Prev / Next navigation ── */
  const currentIndex = useMemo(() => ALL_DOCS.findIndex(d => d.id === selectedDoc), [selectedDoc])
  const prevDoc = currentIndex > 0 ? ALL_DOCS[currentIndex - 1] : null
  const nextDoc = currentIndex < ALL_DOCS.length - 1 ? ALL_DOCS[currentIndex + 1] : null

  /* ── Page title (extracted from raw markdown) ── */
  const pageTitle = useMemo(() => {
    if (selectedDoc === 'search') {
      const q = searchParams.get('q') ?? ''
      return q ? `Search: “${q}”` : 'Search Results'
    }
    const match = content.match(/^#\s+(.+)$/m)
    return match ? match[1].trim() : ALL_DOCS.find(d => d.id === selectedDoc)?.title ?? ''
  }, [content, selectedDoc, searchParams])

  /* ── Custom react-markdown renderers ── */
  const handleDocLink = useCallback(
    (href: string) => {
      const name = href.replace(/\.md$/, '').split('/').pop()?.split('#')[0] || ''
      const docId = LINK_MAP[name]
      if (docId) selectDoc(docId)
    },
    [selectDoc]
  )

  const mdComponents: any = useMemo(
    () => ({
      h1: () => null,
      img: ({ src, alt, ...props }: React.ImgHTMLAttributes<HTMLImageElement>) => {
        const resolved = src ? IMAGE_MAP[src] ?? src : ''
        return (
          <img
            {...props}
            src={resolved}
            alt={alt ?? ''}
            style={{
              maxWidth: '100%',
              borderRadius: '8px',
              margin: '16px 0',
              border: '1px solid var(--mav-border)'
            }}
          />
        )
      },
      pre: ({ children, ...props }: any) => {
        const child = Array.isArray(children) ? children[0] : children
        if (child && typeof child === 'object' && child.props?.className === 'language-panel') {
          return <>{children}</>
        }
        return <pre {...props}>{children}</pre>
      },
      code: ({ className, children, ...props }: any) => {
        if (className === 'language-panel') {
          const text = String(children).replace(/\n$/, '')
          const typeMatch = text.match(/^%%PANEL:(\w+)%%\n?/)
          const panelType = typeMatch ? typeMatch[1] : 'info'
          const panelContent = typeMatch ? text.slice(typeMatch[0].length) : text
          return (
            <div className={`mav-panel mav-panel-${panelType}`}>
              <div className="mav-panel-body">
                <ReactMarkdown
                  remarkPlugins={[remarkGfm]}
                  rehypePlugins={[rehypeRaw, rehypeSlug]}
                  components={mdComponentsRef.current}>
                  {panelContent}
                </ReactMarkdown>
              </div>
            </div>
          )
        }
        return (
          <code className={className} {...props}>
            {children}
          </code>
        )
      },
      a: ({ href, children, ...props }: React.AnchorHTMLAttributes<HTMLAnchorElement>) => {
        // Same-page fragment links (TOC entries, cross-references) — scroll to the target
        // element by id rather than opening a new tab (HashRouter owns the URL hash).
        if (href && href.startsWith('#')) {
          const targetId = decodeURIComponent(href.slice(1))
          return (
            <a
              {...props}
              href={href}
              onClick={e => {
                e.preventDefault()
                document
                  .getElementById(targetId)
                  ?.scrollIntoView({ behavior: 'smooth', block: 'start' })
              }}
              style={{ color: 'var(--mav-blue)', cursor: 'pointer' }}>
              {children}
            </a>
          )
        }
        if (
          href &&
          !href.startsWith('http') &&
          !href.startsWith('#') &&
          !href.startsWith('mailto:')
        ) {
          const name = href.replace(/\.md$/, '').split('/').pop()?.split('#')[0] || ''
          if (LINK_MAP[name]) {
            return (
              <a
                {...props}
                href="#"
                onClick={e => {
                  e.preventDefault()
                  handleDocLink(href)
                }}
                style={{ color: 'var(--mav-blue)', cursor: 'pointer' }}>
                {children}
              </a>
            )
          }
        }
        return (
          <a {...props} href={href} target="_blank" rel="noopener noreferrer">
            {children}
          </a>
        )
      }
    }),
    [handleDocLink]
  )
  mdComponentsRef.current = mdComponents

  /* ── Sidebar tree renderer ── */
  const renderTree = (nodes: DocNode[], depth = 0) =>
    nodes.map(node => {
      const isActive = selectedDoc === node.id
      const hasChildren = !!node.children?.length
      const isExpanded = expandedNodes.has(node.id)

      return (
        <div key={node.id}>
          <div
            className={`mav-sidebar-item${isActive ? ' active' : ''}${depth > 0 ? ' child' : ''}`}
            style={{ paddingLeft: 20 + depth * 20 }}
            onClick={() => {
              selectDoc(node.id)
              if (hasChildren) toggleExpand(node.id)
            }}>
            {hasChildren && (
              <span
                className="mav-sidebar-chevron"
                onClick={e => {
                  e.stopPropagation()
                  toggleExpand(node.id)
                }}>
                <ChevronIcon expanded={isExpanded} />
              </span>
            )}
            <span className="mav-sidebar-label">{node.title}</span>
          </div>
          {hasChildren && isExpanded && renderTree(node.children!, depth + 1)}
        </div>
      )
    })

  /* ═══════════ Render ═══════════ */
  return (
    <>
      <style>{`
        :root {
          --mav-blue: rgb(0, 114, 206);
          --mav-blue-dark: rgb(0, 90, 165);
          --mav-blue-light: rgb(229, 242, 255);
          --mav-text: #1a1a2e;
          --mav-muted: #5a6475;
          --mav-border: #e2e8f0;
        }

        ${MAV_NAV_CSS}

        /* ── PAGE WRAPPER ── */
        .mav-page-wrapper {
          width: calc(100% + 4rem);
          margin-left: -2rem;
          margin-right: -2rem;
          align-self: stretch;
          display: flex;
          flex-direction: column;
          height: 100vh;
        }

        /* ── DOCS LAYOUT ── */
        .mav-docs-layout {
          display: flex;
          flex: 1;
          min-height: 0;
          overflow: hidden;
        }

        /* ── SIDEBAR ── */
        .mav-sidebar {
          width: 280px;
          flex-shrink: 0;
          border-right: 1px solid var(--mav-border);
          background: #f8fafc;
          overflow-y: auto;
          padding: 16px 0;
        }
        .mav-sidebar-heading {
          padding: 8px 20px 14px;
          font-size: .7rem;
          font-weight: 700;
          letter-spacing: .1em;
          text-transform: uppercase;
          color: var(--mav-muted);
        }
        .mav-sidebar-item {
          display: flex;
          align-items: center;
          padding: 7px 20px;
          cursor: pointer;
          font-size: .875rem;
          color: var(--mav-text);
          transition: background .12s, color .12s;
          user-select: none;
          gap: 6px;
        }
        .mav-sidebar-item:hover {
          background: var(--mav-blue-light);
          color: var(--mav-blue);
        }
        .mav-sidebar-item.active {
          color: var(--mav-blue);
          background: var(--mav-blue-light);
          font-weight: 600;
        }
        .mav-sidebar-item.child {
          font-size: .82rem;
          color: var(--mav-muted);
        }
        .mav-sidebar-item.child:hover,
        .mav-sidebar-item.child.active {
          color: var(--mav-blue);
        }
        .mav-sidebar-chevron {
          display: flex;
          align-items: center;
          justify-content: center;
          color: var(--mav-muted);
        }
        .mav-sidebar-label {
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        /* ── CONTENT PANE ── */
        .mav-content {
          flex: 1;
          display: flex;
          flex-direction: column;
          overflow: hidden;
          background: #fff;
        }
        .mav-content-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 24px;
          padding: 16px 56px;
          border-bottom: 1px solid var(--mav-border);
          background: #fff;
          flex-shrink: 0;
        }
        .mav-page-title {
          font-size: 1.5rem;
          font-weight: 800;
          color: var(--mav-text);
          letter-spacing: -.02em;
          margin: 0;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          min-width: 0;
        }
        .mav-content-body {
          flex: 1;
          overflow-y: auto;
          padding: 32px 56px 80px;
        }
        .mav-content-inner {
          max-width: 860px;
          margin: 0 auto;
        }
        .mav-loading {
          display: flex;
          align-items: center;
          justify-content: center;
          height: 200px;
          color: var(--mav-muted);
          font-size: .95rem;
        }

        /* ── MARKDOWN TYPOGRAPHY ── */
        .mav-md h1 { display: none; }
        .mav-md h2 {
          font-size: 1.4rem;
          font-weight: 700;
          color: var(--mav-text);
          margin: 36px 0 12px;
          padding-bottom: 8px;
          border-bottom: 1px solid var(--mav-border);
        }
        .mav-md h3 {
          font-size: 1.1rem;
          font-weight: 600;
          color: var(--mav-text);
          margin: 28px 0 8px;
        }
        .mav-md p {
          font-size: .95rem;
          line-height: 1.7;
          color: var(--mav-text);
          margin: 0 0 16px;
        }
        .mav-md ul, .mav-md ol {
          margin: 0 0 16px;
          padding-left: 24px;
        }
        .mav-md li {
          font-size: .95rem;
          line-height: 1.7;
          color: var(--mav-text);
          margin-bottom: 4px;
        }
        .mav-md a {
          color: var(--mav-blue);
          text-decoration: none;
        }
        .mav-md a:hover { text-decoration: underline; }
        .mav-md blockquote {
          margin: 16px 0;
          padding: 12px 20px;
          border-left: 4px solid var(--mav-blue);
          background: var(--mav-blue-light);
          border-radius: 0 8px 8px 0;
        }
        .mav-md blockquote p { margin: 0; }
        .mav-md table {
          width: 100%;
          border-collapse: collapse;
          margin: 16px 0 24px;
          font-size: .9rem;
        }
        .mav-md thead th {
          background: var(--mav-blue-light);
          color: var(--mav-blue-dark);
          font-weight: 700;
          text-align: left;
          padding: 10px 14px;
          border-bottom: 2px solid var(--mav-blue);
        }
        .mav-md tbody td {
          padding: 10px 14px;
          border-bottom: 1px solid var(--mav-border);
          color: var(--mav-text);
        }
        .mav-md tbody tr:last-child td { border-bottom: none; }
        .mav-md tbody tr:hover { background: rgba(0, 114, 206, .04); }
        .mav-md code {
          background: #f1f5f9;
          padding: 2px 6px;
          border-radius: 4px;
          font-size: .85rem;
          color: #c026d3;
        }
        .mav-md pre {
          background: #0f172a;
          color: #e2e8f0;
          padding: 20px;
          border-radius: 10px;
          overflow-x: auto;
          margin: 16px 0 24px;
        }
        .mav-md pre code {
          background: none;
          padding: 0;
          color: inherit;
        }

        /* ── PANELS (info / note tips) ── */
        .mav-panel {
          margin: 20px 0;
          padding: 16px 20px;
          border-radius: 10px;
          border-left: 4px solid;
        }
        .mav-panel-info {
          border-color: #3b82f6;
          background: #eff6ff;
        }
        .mav-panel-note {
          border-color: #f59e0b;
          background: #fffbeb;
        }
        .mav-panel-header {
          display: flex;
          align-items: center;
          gap: 6px;
          margin-bottom: 6px;
        }
        .mav-panel-icon {
          font-size: 1.1rem;
        }
        .mav-panel-label {
          font-weight: 700;
          font-size: .85rem;
          text-transform: uppercase;
          letter-spacing: .04em;
        }
        .mav-panel-info .mav-panel-label { color: #2563eb; }
        .mav-panel-note .mav-panel-label { color: #d97706; }
        .mav-panel-body {
          font-size: .93rem;
          line-height: 1.65;
          color: var(--mav-text);
        }
        .mav-panel-body p:last-child { margin-bottom: 0; }
        body.dark .mav-panel-info {
          background: rgba(59, 130, 246, .1);
          border-color: #60a5fa;
        }
        body.dark .mav-panel-note {
          background: rgba(245, 158, 11, .1);
          border-color: #fbbf24;
        }
        body.dark .mav-panel-info .mav-panel-label { color: #93bbfd; }
        body.dark .mav-panel-note .mav-panel-label { color: #fbbf24; }
        .mav-md img {
          max-width: 100%;
          border-radius: 8px;
          margin: 16px 0;
          border: 1px solid var(--mav-border);
        }
        .mav-md hr {
          border: none;
          height: 1px;
          background: var(--mav-border);
          margin: 32px 0;
        }

        /* ── FOOTER ── */
        .mav-footer {
          background: #f8fafc;
          border-top: 1px solid var(--mav-border);
          padding: 20px 48px;
          display: flex;
          align-items: center;
          justify-content: space-between;
          flex-shrink: 0;
        }
        .mav-footer-brand { font-weight: 700; color: var(--mav-blue); font-size: 1rem; }
        .mav-footer p { font-size: .82rem; color: #9aa3b0; }
        .mav-footer-links { display: flex; gap: 24px; }
        .mav-footer-links a { font-size: .82rem; color: var(--mav-muted); text-decoration: none; }
        .mav-footer-links a:hover { color: var(--mav-blue); }

        /* ── DARK MODE ── */
        body.dark {
          --mav-blue-light: rgba(0, 114, 206, .15);
          --mav-text: #e2e8f0;
          --mav-muted: #94a3b8;
          --mav-border: #2d3748;
        }
        body.dark .mav-sidebar { background: #0c1018; }
        body.dark .mav-content { background: #111827; }
        body.dark .mav-content-header { background: #111827; border-bottom-color: var(--mav-border); }
        body.dark .mav-footer {
          background: #080d1a;
          border-top-color: var(--mav-border);
        }
        body.dark .mav-footer p { color: #64748b; }
        body.dark .mav-md code {
          background: #1e293b;
          color: #f97316;
        }
        body.dark .mav-md pre { background: #020617; }
        body.dark .mav-md thead th { background: rgba(0, 114, 206, .2); }
        body.dark .mav-md tbody tr:hover { background: rgba(0, 114, 206, .08); }
        body.dark .mav-md blockquote { background: rgba(0, 114, 206, .1); }

        /* ── DOC PREV/NEXT NAV (header) ── */
        .mav-doc-nav-header {
          display: flex;
          gap: 8px;
          flex-shrink: 0;
        }
        .mav-doc-nav-btn {
          display: flex;
          align-items: center;
          gap: 4px;
          padding: 6px 12px;
          border: 1px solid var(--mav-border);
          border-radius: 8px;
          cursor: pointer;
          background: none;
          font-size: .82rem;
          font-weight: 600;
          color: var(--mav-blue);
          white-space: nowrap;
          transition: border-color .15s, background .15s;
        }
        .mav-doc-nav-btn:hover {
          border-color: var(--mav-blue);
          background: var(--mav-blue-light);
        }
        body.dark .mav-doc-nav-btn:hover { background: rgba(0, 114, 206, .12); }

        /* ── SEARCH RESULTS PAGE ── */
        .mav-search-page-empty {
          padding: 48px 0;
          font-size: .95rem;
          color: var(--mav-muted);
        }
        .mav-search-page-count {
          font-size: .85rem;
          color: var(--mav-muted);
          margin-bottom: 24px;
        }
        .mav-search-page-results {
          display: flex;
          flex-direction: column;
          gap: 12px;
        }
        .mav-search-page-card {
          padding: 20px 24px;
          border: 1px solid var(--mav-border);
          border-radius: 10px;
          cursor: pointer;
          transition: border-color .15s, background .15s;
        }
        .mav-search-page-card:hover {
          border-color: var(--mav-blue);
          background: var(--mav-blue-light);
        }
        .mav-search-page-card-title {
          font-size: 1rem;
          font-weight: 700;
          color: var(--mav-blue);
          margin-bottom: 8px;
        }
        .mav-search-page-card-snippet {
          font-size: .875rem;
          color: var(--mav-muted);
          line-height: 1.65;
        }
        body.dark .mav-search-page-card:hover { background: rgba(0, 114, 206, .1); }

        @media (max-width: 768px) {
          .mav-nav { padding: 0 20px; }
          .mav-sidebar { width: 220px; }
          .mav-content-header { padding: 12px 20px; }
          .mav-content-body { padding: 24px 20px 60px; }
        }
      `}</style>

      <div className="mav-page-wrapper">
        {/* NAV */}
        <MavNavBar onSearchSelect={selectDoc} />

        {/* SIDEBAR + CONTENT */}
        <div className="mav-docs-layout">
          <aside className="mav-sidebar">
            <div className="mav-sidebar-heading">Documentation</div>
            {renderTree(DOC_TREE)}
          </aside>

          <main className="mav-content">
            <div className="mav-content-header">
              <h1 className="mav-page-title">{loading ? '' : pageTitle}</h1>
              {!loading && (prevDoc || nextDoc) && (
                <div className="mav-doc-nav-header">
                  {prevDoc && (
                    <button className="mav-doc-nav-btn" onClick={() => selectDoc(prevDoc.id)}>
                      ← {prevDoc.title}
                    </button>
                  )}
                  {nextDoc && (
                    <button className="mav-doc-nav-btn" onClick={() => selectDoc(nextDoc.id)}>
                      {nextDoc.title} →
                    </button>
                  )}
                </div>
              )}
            </div>
            <div className="mav-content-body" ref={contentRef}>
              <div className="mav-content-inner">
                {selectedDoc === 'search' ? (
                  searchPageLoading ? (
                    <div className="mav-loading">Searching…</div>
                  ) : searchPageResults.length === 0 ? (
                    <div className="mav-search-page-empty">
                      No results found for <strong>&ldquo;{searchParams.get('q')}&rdquo;</strong>.
                    </div>
                  ) : (
                    <div className="mav-search-page-results">
                      <p className="mav-search-page-count">
                        {searchPageResults.length} result{searchPageResults.length !== 1 ? 's' : ''}
                      </p>
                      {searchPageResults.map(r => (
                        <div
                          key={r.docId}
                          className="mav-search-page-card"
                          onClick={() => selectDoc(r.docId)}>
                          <div className="mav-search-page-card-title">{r.title}</div>
                          <div className="mav-search-page-card-snippet">{r.snippet}</div>
                        </div>
                      ))}
                    </div>
                  )
                ) : loading ? (
                  <div className="mav-loading">Loading…</div>
                ) : (
                  <div className="mav-md">
                    <ReactMarkdown
                      remarkPlugins={[remarkGfm]}
                      rehypePlugins={[rehypeRaw, rehypeSlug]}
                      components={mdComponents}>
                      {preprocessPanels(content)}
                    </ReactMarkdown>
                  </div>
                )}
              </div>
            </div>
          </main>
        </div>

        {/* FOOTER */}
        <footer className="mav-footer">
          <div>
            <div className="mav-footer-brand">MAV-Aero</div>
            <p style={{ marginTop: '4px' }}>© 2026 GM Motorsports. All rights reserved.</p>
          </div>
          <div className="mav-footer-links">
            <Link to="/mav-aero" style={{ textDecoration: 'none' }}>
              Home
            </Link>
            <Link to="/mav-aero-docs" style={{ textDecoration: 'none' }}>
              Docs
            </Link>
            <a href="https://gm.enterprise.slack.com/archives/C09SANE099D">Support</a>
          </div>
        </footer>
      </div>
    </>
  )
}

export default MAVAero
